import { createContext, useContext, useState, useEffect } from 'react';
import type { PropsWithChildren } from 'react';

interface LayoutContextValue {
  isMobile: boolean;
  isDesktop: boolean;
  layout: 'mobile' | 'desktop';
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

const MOBILE_BREAKPOINT = 768;

export function LayoutProvider({ children }: PropsWithChildren) {
  // Determine layout based on screen width
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false
  );

  // Listen for window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <LayoutContext.Provider value={{
      isMobile,
      isDesktop: !isMobile,
      layout: isMobile ? 'mobile' : 'desktop',
    }}>
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayout(): LayoutContextValue {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
}

export default LayoutContext;
